import React, {useContext, useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import toast from "react-hot-toast";

import WideSecureLayout from "../../layout/WideSecureLayout.jsx";
import FormControl from "../../components/form/FormControl.jsx";
import ProjectProfileLogo from "./ProjectProfileLogo.jsx";
import DeleteProjectContent from "./DeleteProjectContent.jsx";

import {ProjectApi, ProjectPermission} from "../../api/authority/generated/index.ts";
import useDownloadState from "../../api/utilities/useDownloadState.js";
import {GlobalContext} from "../../utils/context/GlobalContextProvider.jsx";
import {useErrorTranslation} from "../../api/utilities/translateErrorMessage.js";
import BillingDataForm from "./Billing/BillingDataForm.jsx";
import CardForm from "./Billing/CardForm.jsx";
import Button from "../../components/Button.jsx";
import {Panel} from "../admin/ReportingLab.jsx";
import {useIsAdministrator} from "../../components/auth/rolesAndPermissions.js";
import InvoiceList from "./Billing/InvoiceList.jsx";
import {useHasPermission} from "../../components/auth/rolesAndPermissions.js";
import ProjectMcpGrants, {getProjectSettingsAccess} from "./ThirdPartyServiceIntegrations/ProjectMcpGrants.jsx";

export default function ProjectSettings({project}) {
    const {t} = useTranslation();
    const {et} = useErrorTranslation();
    const {context, getAuthorityConfiguration, resetCurrentUserProjects} = useContext(GlobalContext);
    const isAdministrator = useIsAdministrator();
    const hasPermission = useHasPermission();
    const isProjectMember = context.currentUserProjects.some((candidate) => candidate.id === project.id);
    const {canEditProfile, canViewBilling, canViewMcp} = getProjectSettingsAccess(
        project,
        hasPermission,
        isProjectMember,
        isAdministrator,
    );

    const initialProjectInputState = {
        name: project.name,
        logoUrl: project.logoUrl,
        currencyCode: project.currency || 'CZK',
    };

    const [projectInput, setProjectInput] = useState(initialProjectInputState);
    const [newUri, setNewUri] = useState('');
    const [saveState, setDownloading, setResult, setError] = useDownloadState();

    useEffect(() => {
        setProjectInput(initialProjectInputState);
    }, [project.id]);

    const handleSaveProject = async () => {
        setDownloading(true);
        try {
            const projectPayload = {...projectInput};
            const response = await new ProjectApi(getAuthorityConfiguration()).updateProject({
                projectId: project.id,
                projectInput: projectPayload,
            });
            setResult(response);
            toast.success(t('saved'));
            resetCurrentUserProjects();
        } catch (e) {
            const errorMessage = await et(e);
            toast.error(errorMessage);
        } finally {
            setDownloading(false);
        }
    };

    return (
        <WideSecureLayout title={t('route.project-settings')} titleAlignment="left" compactHeaderSpacing>
            <div className="grid gap-6 xl:grid-cols-2 xl:items-start">
                <div className="grid gap-6">
                    {canEditProfile && <Panel title={t('project.misc-settings')} loading={saveState.downloading} contentClassName="p-5">
                            <ProjectProfileLogo project={project}/>

                            <FormControl
                                className="mt-5"
                                key={'name' + project.id}
                                type={'text'}
                                variant="modern"
                                label={t('project.name')}
                                value={projectInput.name}
                                onChange={(e) => setProjectInput({...projectInput, name: e.target.value})}
                            />
                            <label className="mt-4 block">
                                <span className="mb-1.5 block text-sm text-slate-700 dark:text-slate-300">{t('project.currency')}</span>
                                <select
                                    value={projectInput.currencyCode}
                                    onChange={(e) => setProjectInput({...projectInput, currencyCode: e.target.value})}
                                    className="form-select w-full rounded-lg border-slate-300 dark:border-slate-600 dark:bg-slate-800"
                                >
                                    <option value="CZK">CZK (Kč)</option>
                                    <option value="EUR">EUR (€)</option>
                                </select>
                            </label>
                            <div className="mt-5 flex justify-end">
                                <Button size="normal" onClick={handleSaveProject}>
                                    {t('save')}
                                </Button>
                            </div>
                    </Panel>}

                    {canViewBilling && <Panel title={t('project.payment-data')} contentClassName="p-5">
                        <CardForm project={project}/>
                    </Panel>}

                    {canViewBilling && <Panel title={t('billing.invoices.title')} contentClassName="p-5">
                        <InvoiceList key={project.id} project={project}/>
                    </Panel>}

                    {canEditProfile && <Panel title={t('project.delete')} className="hidden border-red-200 xl:block dark:border-red-900/60" contentClassName="p-5">
                        <DeleteProjectContent project={project}/>
                    </Panel>}
                </div>

                <div className="grid gap-6">
                    {canViewBilling && <Panel title={t('project.billing-data')} contentClassName="p-5">
                        <BillingDataForm project={project} onSave={resetCurrentUserProjects}/>
                    </Panel>}

                    {canViewMcp && <Panel title={t('project.mcp.box-title', {defaultValue: 'MCP pro AI asistenty'})} contentClassName="p-5">
                        <ProjectMcpGrants project={project} isAdministrator={isAdministrator}/>
                    </Panel>}
                </div>

                {canEditProfile && <Panel title={t('project.delete')} className="border-red-200 xl:hidden dark:border-red-900/60" contentClassName="p-5">
                    <DeleteProjectContent project={project}/>
                </Panel>}
            </div>
        </WideSecureLayout>
    );
}
